import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import { CfsApi } from '@/api';
import {
  AppModal,
  FileGrid,
  FilePickerDirection,
  FormRoot,
  RenderFormField,
} from '@/components';
import { withLoader, WithLoaderProps } from '@/hocs';
import { useCFSContext } from '@/hooks';
import { PageBackHeader, TabAnimationWrapper } from '@/layouts/TabLayout';
import { IFile } from '@/models';
import { FileAcceptedTypes } from '@/pages/Kases/KaseFiles/components/data';
import { isCFSViewOnly, uploadFiles } from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

export const CFSMediaFormFields = [
  {
    key: 'photos',
    title: 'Photos',
    items: [
      {
        name: 'files',
        type: 'file',
        label: '',
        direction: 'horizontal' as FilePickerDirection,
        grid: {
          md: 12,
          lg: 12,
        },
      },
    ],
  },
];

type CFSMediaForm = {
  files: IFile[];
};

const CFSMedia = ({ showLoader, hideLoader }: WithLoaderProps) => {
  const navigate = useNavigate();
  const { cfs, updateCFS } = useCFSContext();
  const { user } = useUserStore();
  const readOnly = isCFSViewOnly(user);
  const [files, setFiles] = useState<IFile[]>([]);
  const { updateToast } = useToastStore();
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState(8);
  const [fileToDelete, setFileToDelete] = useState<number | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const methods = useForm<CFSMediaForm>({
    defaultValues: { files: [] },
    mode: 'all',
  });
  const { handleSubmit, watch } = methods;

  // Watch for changes to the files field
  const watchedFiles = watch('files');

  useEffect(() => {
    if (!cfs) {
      return;
    }
    setFiles(cfs.files || []);
  }, [cfs, cfs?.files]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1250) {
        setCols(4);
      } else if (window.innerWidth < 1800) {
        setCols(6);
      } else {
        setCols(8);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Automatically submit the form when files are added
  useEffect(() => {
    if (watchedFiles && watchedFiles.length > 0) {
      handleSubmit(handleFormSubmit)();
    }
  }, [watchedFiles]);

  const handleClose = () => {
    setFileToDelete(null);
    setOpen(false);
  };

  const handleFormSubmit = async (values: any) => {
    if (!cfs?._id) {
      updateToast({ open: true, message: 'Invalid CFS ID' });
      return;
    }

    files?.forEach((file) => {
      file.uploadedBy = undefined;
    });

    try {
      showLoader();
      let uploadedFiles = await uploadFiles(values.files || []);

      if (!uploadedFiles) return;

      if (files) {
        uploadedFiles = uploadedFiles.concat(files);
      }

      const response = await CfsApi.uploadFiles(cfs._id, {
        files: uploadedFiles,
      });

      if (response.status !== 200) {
        throw new Error(response.statusText);
      }

      updateToast({
        open: true,
        message: 'File uploaded successfully!',
        type: ConfirmTypes.SUCCESS,
      });

      const cfsFiles = response.data;
      updateCFS({ ...cfs, files: cfsFiles });

      // Reset form state completely
      methods.setValue('files', []);
      methods.reset({ files: [] });

      // Reset the file input element
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Force re-render of the file input component
      setResetKey((prev) => prev + 1);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  const handleOnFileDelete = async (index: number) => {
    setFileToDelete(index);
    setOpen(true);
  };

  const deleteFile = async () => {
    if (!cfs?._id) {
      return;
    }

    if (fileToDelete === null) {
      return;
    }

    // remove index form files
    const newFiles = files.filter((_, i) => i !== fileToDelete);
    CfsApi.uploadFiles(cfs._id, { files: newFiles })
      .then(() => {
        updateToast({
          open: true,
          message: 'File deleted successfully!',
          type: ConfirmTypes.SUCCESS,
        });
      })
      .catch(() => {
        updateToast({
          open: true,
          message: `Error deleting file!`,
          type: ConfirmTypes.ERROR,
        });
      });
    handleClose();
  };

  return (
    <TabAnimationWrapper>
      <Stack
        display="flex"
        height="100%"
        flexDirection="column"
        overflow="auto"
      >
        <PageBackHeader
          title="Media"
          style={{ fontWeight: 500 }}
          goBack={() => navigate(-1)}
          backButtonSx={{ color: 'error.main' }}
        />

        <Typography style={{ marginBottom: 20, marginTop: 10 }}>
          Photos and videos
        </Typography>
        <Stack flex={1}>
          {files.length === 0 && (
            <Typography>No files available to display</Typography>
          )}
          <FileGrid
            files={files}
            cols={cols}
            onDelete={handleOnFileDelete}
            readOnly={readOnly}
          >
            {!readOnly && (
              <Box>
                <FormProvider {...methods}>
                  <FormRoot
                    onSubmit={handleSubmit(handleFormSubmit)}
                    className="file-add-form"
                    key={resetKey}
                  >
                    {CFSMediaFormFields.map(({ items }) => {
                      return items.map(({ grid, ...rest }, index) => (
                        <RenderFormField
                          disablePreview={true}
                          isInFileGrid={true}
                          key={index}
                          {...rest}
                          accept={FileAcceptedTypes}
                          inputRef={fileInputRef}
                        />
                      ));
                    })}
                  </FormRoot>
                </FormProvider>
              </Box>
            )}
          </FileGrid>
        </Stack>
        <AppModal
          open={open}
          onClose={handleClose}
          title="Are you sure you want to delete this file?"
        >
          <Stack spacing={2}>
            <Typography variant="body1">
              Once deleted, this file cannot be recovered. Do you want to
              proceed?
            </Typography>
            <Divider />
            <Stack flexDirection="row" justifyContent="end">
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                sx={{ ml: 2 }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="error"
                size="large"
                sx={{ ml: 2 }}
                onClick={deleteFile}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </AppModal>
      </Stack>
    </TabAnimationWrapper>
  );
};

export default withLoader(CFSMedia);
